import { useState, useEffect } from "react";
import { useAtomValue } from "jotai";
import { useProductApi } from "@/hooks/apiHooks/useProductApi";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { useCatalogApi } from "@/hooks/apiHooks/useCatalogApi";
import { optionsAtom } from "@/store/optionAtoms";
import { activeOptionValues } from "@admin/Inventory/_helpers/activeOptionValues";
import { Button, Label } from "@common/index";
import { Input } from "@common/Form/Input";
import type { CategoryResponse } from "@models/category";
import type { Product } from "@models/product";
import { ComponentColor } from "@common/types";

const flattenCategories = (
    cats: CategoryResponse[],
    path: string[] = [],
): Array<{ id: number; label: string }> => {
    const result: Array<{ id: number; label: string }> = [];
    cats.forEach((cat) => {
        const currentPath = [...path, cat.name];
        result.push({ id: cat.id, label: currentPath.join(" / ") });
        if (cat.children?.length > 0) {
            result.push(...flattenCategories(cat.children, currentPath));
        }
    });
    return result;
};

export const ProductDrydock = () => {
    const { createProduct, getProducts } = useProductApi();
    const { getCategories } = useCategoryApi();
    const { createCatalog } = useCatalogApi();

    const options = useAtomValue(optionsAtom);

    const [flatCategories, setFlatCategories] = useState<Array<{ id: number; label: string }>>([]);
    const [products, setProducts] = useState<Product[]>([]);

    // form state
    const [categoryId, setCategoryId] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [brand, setBrand] = useState("");
    const [upc, setUpc] = useState("");
    const [ean, setEan] = useState("");
    const [manufacturerSku, setManufacturerSku] = useState("");
    const [msrp, setMsrp] = useState("");
    const [serialized, setSerialized] = useState(false);
    const [discountAllowed, setDiscountAllowed] = useState(true);

    // variant[0] state
    const [variantName, setVariantName] = useState("");
    const [variantDescription, setVariantDescription] = useState("");
    const [variantMsrp, setVariantMsrp] = useState("");
    const [selectedOptionValueIds, setSelectedOptionValueIds] = useState<number[]>([]);

    const toggleOptionValue = (id: number) => {
        setSelectedOptionValueIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    useEffect(() => {
        getCategories().then((data) => setFlatCategories(flattenCategories(data)));
    }, [getCategories]);

    const handlePopulate = () => {
        const items = ["Widget", "Gadget", "Device", "Module", "Unit"];
        const brands = ["Acme", "TechPro", "InnoVate", "FastBuild", "ProLine"];
        const rand = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];
        const basePrice = +(Math.random() * 200 + 10).toFixed(2);

        setName(`${rand(items)} ${Math.floor(Math.random() * 1000)}`);
        setDescription(`A quality ${rand(items).toLowerCase()} for professional use`);
        setBrand(rand(brands));
        setUpc(Math.floor(Math.random() * 1e12).toString().padStart(12, "0"));
        setEan(`EAN-${Math.floor(Math.random() * 9000 + 1000)}`);
        setManufacturerSku(`MSKU-${Math.floor(Math.random() * 9000 + 1000)}`);
        setMsrp((basePrice * 1.2).toFixed(2));
        setDiscountAllowed(true);
        setSerialized(false);
        // variant defaults mirror product pricing
        setVariantMsrp((basePrice * 1.2).toFixed(2));
    };

    const handleCreateCatalog = async () => {
        await createCatalog({ name: `Catalog ${new Date().toISOString().slice(0, 10)} #${Math.floor(Math.random() * 100)}`, description: "A catalog description" });
    };

    // A variant goes only once it has a Name and Description (the validator
    // rejects either blank) and the MSRP it always sends — a half-filled one
    // is left out. No Price or Cost on either (P1/P2, rulings 2026-10-29):
    // the API derives both from the vendor offers.
    const hasVariant = Boolean(variantName && variantDescription && variantMsrp);
    const canCreate = Boolean(categoryId && msrp);

    const handleCreate = async () => {
        if (!canCreate) return;

        await createProduct({
            categoryId: parseInt(categoryId),
            serialized,
            discountAllowed,
            msrp: parseFloat(msrp),
            ...(name && { name }),
            ...(description && { description }),
            ...(brand && { brand }),
            ...(upc && { upc }),
            ...(ean && { ean }),
            ...(manufacturerSku && { manufacturerSku }),
            ...(hasVariant
                ? {
                    variants: [{
                        name: variantName,
                        description: variantDescription,
                        ...(selectedOptionValueIds.length > 0 && { options: selectedOptionValueIds }),
                        msrp: parseFloat(variantMsrp),
                    }],
                }
                : {}),
        });
    };

    const handleGetProducts = async () => {
        const response = await getProducts({});
        setProducts(response.items);
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md max-w-4xl">
            <h2 className="text-xl font-bold mb-4">Products</h2>

            <div className="flex gap-2 mb-6">
                <Button onClick={handleCreateCatalog} color={ComponentColor.Secondary}>
                    Create Catalog
                </Button>
                <Button onClick={handlePopulate} color={ComponentColor.Accent}>
                    Populate
                </Button>
            </div>

            <div className="space-y-4 mb-6">
                {/* Category */}
                <div>
                    <Label>Category *</Label>
                    <select
                        className="select select-bordered w-full"
                        value={categoryId}
                        onChange={(e) => setCategoryId(e.target.value)}
                    >
                        <option value="">-- select a category --</option>
                        {flatCategories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                                {cat.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Name / Brand */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>Name</Label>
                        <Input
                            name="name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Product name"
                        />
                    </div>
                    <div>
                        <Label>Brand</Label>
                        <Input
                            name="brand"
                            type="text"
                            value={brand}
                            onChange={(e) => setBrand(e.target.value)}
                            placeholder="Brand"
                        />
                    </div>
                </div>

                {/* Description */}
                <div>
                    <Label>Description</Label>
                    <Input
                        name="description"
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Description"
                    />
                </div>

                {/* Identifiers */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>UPC</Label>
                        <Input name="upc" type="text" value={upc} onChange={(e) => setUpc(e.target.value)} placeholder="UPC" />
                    </div>
                    <div>
                        <Label>EAN</Label>
                        <Input name="ean" type="text" value={ean} onChange={(e) => setEan(e.target.value)} placeholder="EAN" />
                    </div>
                    <div>
                        <Label>Manufacturer SKU</Label>
                        <Input
                            name="manufacturerSku"
                            type="text"
                            value={manufacturerSku}
                            onChange={(e) => setManufacturerSku(e.target.value)}
                            placeholder="Manufacturer SKU"
                        />
                    </div>
                </div>

                {/* Pricing */}
                <div>
                    <Label>MSRP *</Label>
                    <Input name="msrp" type="number" value={msrp} onChange={(e) => setMsrp(e.target.value)} placeholder="0.00" />
                </div>

                {/* Flags */}
                <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            className="checkbox"
                            checked={serialized}
                            onChange={(e) => setSerialized(e.target.checked)}
                        />
                        <span>Serialized</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            className="checkbox"
                            checked={discountAllowed}
                            onChange={(e) => setDiscountAllowed(e.target.checked)}
                        />
                        <span>Discount Allowed</span>
                    </label>
                </div>

                {/* Variant — inline, one only */}
                <div className="border border-dashed border-gray-300 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-700">Variant <span className="text-xs font-normal text-gray-400">#1</span></h3>
                        <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                            The API accepts a list — only one variant is supported here for testing purposes.
                            Use <em>POST /products/&#123;id&#125;/variants</em> to add more after creation.
                        </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Variant Name *</Label>
                            <Input
                                name="variantName"
                                type="text"
                                value={variantName}
                                onChange={(e) => setVariantName(e.target.value)}
                                placeholder="e.g. Red / Large"
                            />
                        </div>
                        <div>
                            <Label>Variant Description *</Label>
                            <Input
                                name="variantDescription"
                                type="text"
                                value={variantDescription}
                                onChange={(e) => setVariantDescription(e.target.value)}
                                placeholder="Description"
                            />
                        </div>
                    </div>

                    <div>
                        <Label>MSRP *</Label>
                        <Input
                            name="variantMsrp"
                            type="number"
                            value={variantMsrp}
                            onChange={(e) => setVariantMsrp(e.target.value)}
                            placeholder={msrp || "0.00"}
                        />
                    </div>

                    {options.length > 0 ? (
                        <div>
                            <Label>Option Values</Label>
                            <div className="grid grid-cols-2 gap-1 mt-1">
                                {options.flatMap((opt) =>
                                    activeOptionValues(opt.values).map((val) => (
                                        <label key={val.id} className="flex items-center gap-2 cursor-pointer text-sm">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm"
                                                checked={selectedOptionValueIds.includes(val.id)}
                                                onChange={() => toggleOptionValue(val.id)}
                                            />
                                            <span className="text-gray-500">{opt.name}</span>
                                            <span className="text-gray-300">→</span>
                                            <span>{val.value}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400">
                            No options loaded — use the Options section to load them, then option values will appear here.
                            They apply to variants added via <em>POST /products/&#123;id&#125;/variants</em> after creation.
                        </p>
                    )}
                </div>

                <div className="flex gap-2">
                    <Button onClick={handleGetProducts} color={ComponentColor.Secondary}>
                        Get All Products
                    </Button>
                    <Button
                        onClick={handleCreate}
                        color={ComponentColor.Primary}
                        disabled={!canCreate}
                    >
                        Create Product
                    </Button>
                </div>
            </div>

            {products.length > 0 && (
                <div className="mt-6 border-t pt-4 overflow-x-auto">
                    <h3 className="text-lg font-semibold mb-3">Products ({products.length}):</h3>
                    <table className="table table-sm w-full">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Category</th>
                                <th>Brand</th>
                                <th>Price</th>
                                <th>Stock</th>
                                <th>Active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p) => (
                                <tr key={p.id}>
                                    <td className="font-mono text-xs">{p.id.slice(0, 8)}…</td>
                                    <td>{p.name}</td>
                                    <td>{p.categoryName}</td>
                                    <td>{p.brand}</td>
                                    <td>${p.price?.toFixed(2)}</td>
                                    <td>{p.stockQuantity}</td>
                                    <td>{p.isActive ? "✓" : "✗"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
